import {
	AiProvider,
	AuthorityClaimMatchSource,
	AuthorityClaimStatus,
	BriefEmailFrequency,
	NavProfile,
	PlanReviewCandidateResolution,
	PlanReviewFindingSeverity,
	PlanReviewRequirementType
} from "constants/field-values";

export const SEARCH_DEBOUNCE_DELAY_MS = 1500;

// Choices for the side navigation profile of an organization. Clearing the field leaves it unset,
// so the role of each user decides.
export const NAV_PROFILE_OPTIONS = [
	{ value: NavProfile.Default, label: "Default" },
	{ value: NavProfile.Ollie, label: "Ollie" }
] as const;

// Values sent to the submit_support_request action.
export const SupportRequestKind = {
	Feedback: "feedback",
	Support: "support"
} as const;

export const SUPPORT_ISSUE_TYPES = ["Bug Report", "Account Issue", "Billing Question", "Other"] as const;

// The property forms no longer collect drawings: whatever belongs to a submittal package
// is uploaded during Plan Review. The section is kept behind this flag, wiring included,
// so bringing it back is a one-line change and existing drawings stay untouched meanwhile.
export const SHOW_DRAWINGS_SECTION = false;

// Cadence choices offered in the My Profile "Settings" section. Display labels for the
// BriefEmailFrequency values stored on users.brief_email_frequency.
export const BRIEF_EMAIL_FREQUENCY_OPTIONS = [
	{ value: BriefEmailFrequency.DAILY, label: "Daily" },
	{ value: BriefEmailFrequency.WEEKLY, label: "Weekly" },
	{ value: BriefEmailFrequency.OFF, label: "Off" }
] as const;

// Inspection type choices offered in the Ollie flows. UI-only: the selected `value`
// is stored on inspection_jobs.inspection_type, but the list itself is not a DB enum.
export const INSPECTION_TYPE_OPTIONS = [
	{ value: "annual", label: "Annual Fire Safety" },
	{ value: "semiannual", label: "Semi-Annual" },
	{ value: "quarterly", label: "Quarterly" },
	{ value: "monthly", label: "Monthly" },
	{ value: "initial", label: "Initial Acceptance" },
	{ value: "fire-final", label: "Fire Final" },
	{ value: "modification", label: "Post-Modification" }
] as const;

// Options for the "Contact Sales to Upgrade" form on the Ollie inspections paywall
// (MWA-836). Values are plain text, mirrored on the backend in
// constants/sales-contact.mjs — keep both in sync.
export const SALES_CONTACT_PROJECT_ROLE_OPTIONS = [
	{ value: "Owner/Developer", label: "Owner/Developer" },
	{ value: "General Contractor", label: "General Contractor" },
	{ value: "Specialty Contractor", label: "Specialty Contractor" }
] as const;

export const SALES_CONTACT_TOTAL_SIZE_OPTIONS = [
	{ value: "< 50,000 sq ft", label: "< 50,000 sq ft" },
	{ value: "50,000 - 250,000 sq ft", label: "50,000 - 250,000 sq ft" },
	{ value: "250,000 - 500,000 sq ft", label: "250,000 - 500,000 sq ft" },
	{ value: "500,000+ sq ft", label: "500,000+ sq ft" }
] as const;

// Where the "Unlock ..." admin nav paywall (MWA-839/841/842) was triggered from.
// Mirrored on the backend as AdminNavPaywallSource in constants/admin-nav-paywall.mjs.
export enum AdminNavPaywallSource {
	DEVICES = "devices",
	INTELLIGENCE = "intelligence"
}

// Business type options for the admin nav paywall forms (Devices/Intelligence,
// MWA-839/841/842). Values are plain text, mirrored on the backend in
// constants/admin-nav-paywall.mjs — keep both in sync.
export const ADMIN_NAV_PAYWALL_BUSINESS_TYPE_OPTIONS = [
	{ value: "General Contractor", label: "General Contractor" },
	{ value: "Fire/Life Safety Systems", label: "Fire/Life Safety Systems" },
	{ value: "Building Technology Integrations", label: "Building Technology Integrations" },
	{ value: "Engineering", label: "Engineering" },
	{ value: "Architecture or Design", label: "Architecture or Design" },
	{ value: "Third Party Inspection", label: "Third Party Inspection" },
	{ value: "Trades (HVAC, Electrical, Plumbing)", label: "Trades (HVAC, Electrical, Plumbing)" },
	{ value: "Other", label: "Other" }
] as const;

// Per-file ceiling for a plan review submittal. This is the upload limit, not the
// model's: a bigger file is downscaled (and split when that is not enough) by the
// backend's PREPARING step. Mirrored on the backend as MAX_UPLOAD_SIZE_BYTES in
// services/plan-review.mjs — keep both in sync.
export const PLAN_REVIEW_MAX_FILE_SIZE_BYTES = 500 * 1024 * 1024;

// Above this the file needs preparing, which adds minutes to the review.
export const PLAN_REVIEW_LARGE_FILE_WARNING_BYTES = 50 * 1024 * 1024;

// Lines the plan review "analyzing" screen cycles through, one every
// PLAN_REVIEW_ANALYZING_MESSAGE_INTERVAL_MS, and stays on the last one until the review ends.
export const PLAN_REVIEW_ANALYZING_MESSAGES = [
	"Organizing your documents...",
	"Putting on my reading glasses...",
	"Making an espresso…",
	"Scanning sheets for details...",
	"Reviewing project specifications...",
	"Cross-checking against applicable codes and requirements...",
	"Stretching my wings…",
	"Looking for stamps and seals...",
	"Going through all the fine print...",
	"Double-checking my notes...",
	"Thinking about eating a juicy mouse…",
	"Working through compliance findings...",
	"Promise I'm not napping, even though I stayed up all night…",
	"Finalizing my report. Hang tight! This can take a minute or two..."
];

export const PLAN_REVIEW_ANALYZING_MESSAGE_INTERVAL_MS = 3000;

// Choices offered on the AHJ rules screen. Display labels for values the backend
// mirrors in constants/field-values.mjs.
export const PLAN_REVIEW_REQUIREMENT_TYPE_OPTIONS = [
	{ value: PlanReviewRequirementType.SUBMITTAL, label: "Submittal process" },
	{ value: PlanReviewRequirementType.CODE, label: "Code compliance" }
] as const;

export const PLAN_REVIEW_SEVERITY_OPTIONS = [
	{ value: PlanReviewFindingSeverity.CRITICAL, label: "Critical" },
	{ value: PlanReviewFindingSeverity.HIGH, label: "High" },
	{ value: PlanReviewFindingSeverity.MEDIUM, label: "Medium" },
	{ value: PlanReviewFindingSeverity.INFO, label: "Info" }
] as const;

// What the reviewer can do with an extracted rule that collides with an existing one,
// in the order the buttons are laid out.
export const PLAN_REVIEW_RESOLUTION_OPTIONS = [
	{ value: PlanReviewCandidateResolution.IMPORT, label: "Import" },
	{ value: PlanReviewCandidateResolution.REPLACE, label: "Replace" },
	{ value: PlanReviewCandidateResolution.KEEP_BOTH, label: "Keep both" },
	{ value: PlanReviewCandidateResolution.SKIP, label: "Skip" }
] as const;

// What the rule extraction accepts. Gemini reads PDF, plain text and images; a
// spreadsheet or a Word file is not supported and has to be exported to CSV or PDF
// first, so they are kept out of the picker rather than failing after the upload.
export const PLAN_REVIEW_GUIDELINE_ACCEPTED_TYPES = [
	".pdf",
	".txt",
	".csv",
	".md",
	".json",
	".xml",
	".html",
	".png",
	".jpg",
	".jpeg",
	".webp"
].join(",");

// What the Resources screen accepts. A PDF is the usual file; Word, plain text and
// Markdown are read by Doctrine as they are.
export const JURISDICTION_RESOURCE_ACCEPTED_TYPES = [".pdf", ".doc", ".docx", ".txt", ".md"].join(",");

// Per-file ceiling for a guideline document sent to the rule extraction. Mirrors the
// model limit the backend checks before creating the extraction.
export const PLAN_REVIEW_GUIDELINE_MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

// Answer to a create, update or delete on the AHJ's rules, submission types or review
// categories attempted by someone whose role only reads them.
export const PLAN_REVIEW_SETTINGS_READ_ONLY_MESSAGE =
	"Oops, only managers from your organization have permission to change these settings.";

// Optional document classification the AHJ user can set per uploaded file. The AI
// can also infer the type, so this is a convenience, not a hard requirement.
export const PLAN_REVIEW_DOC_TYPE_OPTIONS = [
	{ value: "plans", label: "Plans / Drawings" },
	{ value: "application_form", label: "Application Form" },
	{ value: "cover_letter", label: "Cover Letter" },
	{ value: "calculations", label: "Calculations" },
	{ value: "other", label: "Other" }
] as const;

// Shared options for the "Device Status" / location status filter dropdowns.
// The "null" key represents devices that are not being monitored (device_status === null).
export const DEVICE_STATUS_FILTER_OPTIONS = [
	{ key: "online", label: "Online" },
	{ key: "offline", label: "Offline" },
	{ key: "null", label: "Not Monitored" }
] as const;

// Status shown for a device that the partner integration (myQ) stopped returning. It is not a
// device_status value: in the database device_status is NULL and partner_disconnected_at has a date.
export const DISCONNECTED_DEVICE_STATUS = "disconnected";

// How deeply Claude reasons before it answers. Higher is slower and costs more, and the backend
// owns the list: a level it does not know is shown by its own name.
export const CLAUDE_EFFORT_LABELS: Record<string, string> = {
	low: "Low",
	medium: "Medium",
	high: "High",
	xhigh: "Extra high",
	max: "Max"
};

// Effort a feature starts with when it is switched to Claude from a model that has none.
export const DEFAULT_CLAUDE_EFFORT = "high";

export const AI_PROVIDER_LABELS: Record<AiProvider, string> = {
	[AiProvider.Gemini]: "Gemini",
	[AiProvider.Claude]: "Claude"
};

// The brief reads device ids from text written by the AI model, which can contain a value that is
// not a uuid. Hasura rejects the whole query when one of the values is not a uuid, so the ids are
// checked with this pattern before they are sent.
// Shown in a Brief in place of the property sections when its database part failed and only the
// news came back. Keep in sync with BRIEF_DATABASE_UNAVAILABLE_NOTICE in
// maetro-service/app/constants/briefs.mjs.
export const BRIEF_DATABASE_UNAVAILABLE_NOTICE = {
	title: "Your property updates are not included today",
	body: "We ran into a problem loading the activity of your properties, so this brief only has the public news and reviews we found. This is on our side. Open a chat and ask for your brief to see your property updates."
};

export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The section_number of the code section that represents a whole appendix: only the appendix
// letters ("D", "AJ"). A numbered section always has digits ("D103.4"). Chapters have no section of
// their own, so the citation picker never shows a whole chapter. Same pattern as the service.
export const APPENDIX_SECTION_NUMBER_PATTERN = /^[A-Z]{1,2}$/;

// The word written before the letters of an appendix: "Appendix D".
export const APPENDIX_LABEL_PREFIX = "Appendix";

// Maximum characters of code text that the plan review analysis sends for one cited section,
// with the provisions under it. Same value as SECTION_TEXT_CHARACTER_LIMIT in the service, so the
// code section panel shows the same text that the analysis reads.
export const CODE_SECTION_TEXT_CHARACTER_LIMIT = 40000;

// Steps of the /claim screen, in order.
export enum ClaimJurisdictionStep {
	EMAIL = "email",
	JURISDICTION = "jurisdiction",
	THANKS = "thanks"
}

// What the /claim/setup screen shows after the email link opens it.
export enum ClaimSetupState {
	PREPARING = "preparing", // The checklist that ends on the dashboard.
	UNDER_REVIEW = "underReview",
	REJECTED = "rejected",
	FAILED = "failed"
}

// Query parameter of the email link that carries the email. Same name as CLAIM_EMAIL_PARAM in
// the service (constants/authority-claims.mjs).
export const CLAIM_EMAIL_PARAM = "email";

// Rows of the /claim/setup checklist, in the order they complete.
export const CLAIM_SETUP_PROGRESS_STEPS = [
	"Creating your account and workspace",
	"Organizing rules and adopted code",
	"Getting your project dashboard ready"
];

// Time between two rows of the /claim/setup checklist, and the wait between the last row and the
// dashboard. Same timing as the design prototype.
export const CLAIM_SETUP_PROGRESS_STEP_INTERVAL_MS = 2200;
export const CLAIM_SETUP_REDIRECT_DELAY_MS = 2400;

// Terms and Conditions of the /claim flow.
export const TERMS_AND_CONDITIONS_URL = "https://www.olliebuilt.com/terms";

// Shown on /claim to people who cannot find their jurisdiction in the list or need help.
export const CLAIM_JURISDICTION_SUPPORT_EMAIL = "support@olliebuilt.com";

// Booking page for the 30-minute meeting that finishes the setup of a claimed jurisdiction.
export const SETUP_MEETING_BOOKING_URL = "https://calendly.com/highnoteai/30min";

// State names for the region line of a jurisdiction ("Austin, Texas"). Keyed by the two-letter
// code stored in jurisdictions.state.
export const US_STATE_NAMES: Record<string, string> = {
	AL: "Alabama",
	AK: "Alaska",
	AZ: "Arizona",
	AR: "Arkansas",
	CA: "California",
	CO: "Colorado",
	CT: "Connecticut",
	DE: "Delaware",
	DC: "District of Columbia",
	FL: "Florida",
	GA: "Georgia",
	HI: "Hawaii",
	ID: "Idaho",
	IL: "Illinois",
	IN: "Indiana",
	IA: "Iowa",
	KS: "Kansas",
	KY: "Kentucky",
	LA: "Louisiana",
	ME: "Maine",
	MD: "Maryland",
	MA: "Massachusetts",
	MI: "Michigan",
	MN: "Minnesota",
	MS: "Mississippi",
	MO: "Missouri",
	MT: "Montana",
	NE: "Nebraska",
	NV: "Nevada",
	NH: "New Hampshire",
	NJ: "New Jersey",
	NM: "New Mexico",
	NY: "New York",
	NC: "North Carolina",
	ND: "North Dakota",
	OH: "Ohio",
	OK: "Oklahoma",
	OR: "Oregon",
	PA: "Pennsylvania",
	RI: "Rhode Island",
	SC: "South Carolina",
	SD: "South Dakota",
	TN: "Tennessee",
	TX: "Texas",
	UT: "Utah",
	VT: "Vermont",
	VA: "Virginia",
	WA: "Washington",
	WV: "West Virginia",
	WI: "Wisconsin",
	WY: "Wyoming"
};

// Labels of authority_claims.status on the admin claims screen.
export const AUTHORITY_CLAIM_STATUS_LABELS: Record<AuthorityClaimStatus, string> = {
	[AuthorityClaimStatus.PENDING_VERIFICATION]: "Waiting for email",
	[AuthorityClaimStatus.PENDING_REVIEW]: "Needs review",
	[AuthorityClaimStatus.APPROVED]: "Approved",
	[AuthorityClaimStatus.REJECTED]: "Rejected",
	[AuthorityClaimStatus.COMPLETED]: "Account created"
};

// Labels of authority_claims.match_source on the admin claims screen.
export const AUTHORITY_CLAIM_MATCH_SOURCE_LABELS: Record<AuthorityClaimMatchSource, string> = {
	[AuthorityClaimMatchSource.CONTACT_LIST]: "Known contact",
	[AuthorityClaimMatchSource.EMAIL_DOMAIN]: "Email domain",
	[AuthorityClaimMatchSource.MANUAL_SELECTION]: "Picked from the list",
	[AuthorityClaimMatchSource.ADMIN]: "Created by an admin",
	[AuthorityClaimMatchSource.NEW_JURISDICTION]: "New jurisdiction"
};

// Kinds of authority a person can choose when they add a jurisdiction on /claim. The values are
// codes of jurisdiction_types, the same list as NEW_JURISDICTION_AHJ_TYPES in the service
// (constants/authority-claims.mjs).
export const NEW_JURISDICTION_AHJ_TYPE_OPTIONS = [
	{ label: "City fire department", value: "city_fire_department" },
	{ label: "Emergency services district", value: "emergency_services_district" },
	{ label: "County fire marshal", value: "county_fire_marshal" },
	{ label: "State fire marshal", value: "state_fire_marshal" }
];

// State preselected when a person adds a jurisdiction. The claim flow starts in Texas.
export const NEW_JURISDICTION_DEFAULT_STATE = "TX";
